import { fromLonLat, toLonLat } from 'ol/proj';

/**
 * Geographic WGS84 coordinate. The UI always reads and writes "latitude, longitude";
 * OpenLayers works in [x, y] = [lon, lat], so conversions go through the helpers below.
 */
export interface GeoCoordinate { readonly lat: number; readonly lon: number }

export const COORDINATE_DECIMALS = 5;

export const isValidCoordinate = ({ lat, lon }: GeoCoordinate): boolean =>
  Number.isFinite(lat) && Number.isFinite(lon) && Math.abs(lat) <= 90 && Math.abs(lon) <= 180;

// Both components must carry decimals, so Finca/Suerte codes such as "242 14" or "993-25" are never read as coordinates.
const DECIMAL = String.raw`([+-]?\d{1,3}\.\d+)`;
const COORDINATE_PATTERN = new RegExp(String.raw`^${DECIMAL}\s*°?\s*(?:,\s*|\s+)${DECIMAL}\s*°?$`);

/** "11.53550,-85.85819", "11.53550, -85.85819" or "11.53550 -85.85819" → { lat, lon }; anything else → null. */
export function parseCoordinate(text: string): GeoCoordinate | null {
  const match = COORDINATE_PATTERN.exec(text.trim());
  if (!match) return null;
  const coordinate = { lat: Number(match[1]), lon: Number(match[2]) };
  return isValidCoordinate(coordinate) ? coordinate : null;
}

export const formatCoordinateValue = (value: number): string => value.toFixed(COORDINATE_DECIMALS);

/** "11.53550, -85.85819" (copy format) or, with degrees, "11.53550°, -85.85819°" (status bar). */
export const formatCoordinate = ({ lat, lon }: GeoCoordinate, options: { readonly degrees?: boolean } = {}): string => {
  const unit = options.degrees ? '°' : '';
  return `${formatCoordinateValue(lat)}${unit}, ${formatCoordinateValue(lon)}${unit}`;
};

/** Map (EPSG:3857) coordinate → WGS84. */
export function coordinateFromMap(mapCoordinate: readonly number[]): GeoCoordinate {
  const [lon, lat] = toLonLat([...mapCoordinate]);
  return { lat: lat!, lon: lon! };
}

/** WGS84 → map (EPSG:3857) coordinate. */
export const mapCoordinateFor = ({ lat, lon }: GeoCoordinate): number[] => fromLonLat([lon, lat]);
